/**
 * The boards of the caller's groups, on the dashboard — this module's card
 * type. Its one setting, `board`, is a group's id or `all`.
 *
 * One panel per group whose board has text — or, for one group, that one
 * panel; a session is enough to see them. **Nothing at all while loading, or
 * when there are none**, so a person in no group with a board sees the
 * dashboard exactly as before.
 *
 * With `board.update` each panel has an edit button. Every board shown here is
 * one of the caller's groups, so that is the right set — presentation only,
 * the server checks the permission and the membership again.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { BOARD_PERMISSIONS, type BoardDto } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { ErrorState } from '../../components/ErrorState';
import { MarkdownText } from '../../components/MarkdownText';
import { Panel } from '../../components/Panel';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import type { DashboardOption } from '../types';
import { listOwnBoards } from './api';
import { BoardDialog } from './BoardDialog';

export type BoardCardConfig = { readonly board: string };

/** The card's choices: the boards of all the caller's groups, or one of them. */
export async function boardOptions(): Promise<DashboardOption[]> {
  const { boards } = await listOwnBoards();
  return [
    { value: 'all', labelKey: 'board:dashboard.allBoards' },
    ...boards.map((board) => ({ value: String(board.groupId), label: board.groupName })),
  ];
}

export function DashboardBoards({ config }: { readonly config: BoardCardConfig }) {
  const { t } = useTranslation(['board', 'common']);
  const { can } = useAuth();
  const boards = useAsync(listOwnBoards, []);
  const [editing, setEditing] = useState<BoardDto | null>(null);

  if (boards.error) return <ErrorState error={boards.error} onRetry={boards.reload} />;
  const shown = (boards.data?.boards ?? []).filter(
    (board) => config.board === 'all' || String(board.groupId) === config.board,
  );
  if (shown.length === 0) return null;

  const editable = can(BOARD_PERMISSIONS.UPDATE);

  return (
    <Stack spacing={2}>
      {shown.map((board) => (
        <Panel
          key={board.groupId}
          icon={<CampaignOutlinedIcon fontSize="small" />}
          title={board.groupName}
          actions={
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {board.updatedAt && (
                <Typography variant="caption" color="text.secondary">
                  <Timestamp value={board.updatedAt} />
                </Typography>
              )}
              {editable && (
                <Button size="small" onClick={() => setEditing(board)}>
                  {t('common:action.edit')}
                </Button>
              )}
            </Stack>
          }
        >
          <MarkdownText>{board.text}</MarkdownText>
        </Panel>
      ))}

      {editable && (
        <BoardDialog
          board={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            boards.reload();
          }}
        />
      )}
    </Stack>
  );
}
